// ──────────────────────────────────────────────────────────────────────────────────
// ofraDisplayFormatter.js
//
// This utility converts OFRA calculator values into human-readable text
// that can be displayed in reports and the UI.
//
// For example, it turns:
//   - "peak_milk_can" → "Peak Milk Can (180 mL)"
//   - "urea" → "Urea (1.32 g/mL)"
//   - "point" → "Point"
//
// ──────────────────────────────────────────────────────────────────────────────────
import {
  OFRA_VOLUME_CONTAINER_OPTIONS,
  OFRA_FERTILIZER_TYPE_OPTIONS,
  OFRA_APPLICATION_METHOD_OPTIONS,
} from "../constants/ofraCalculatorOptions";

// ──────────────────────────────────────────────────────────────────────────────────
// Get the human-readable name for a container volume
// For example: peak_milk_can → "Peak Milk Can"
// ──────────────────────────────────────────────────────────────────────────────────
export function getContainerLabel(containerValue) {
  const option = OFRA_VOLUME_CONTAINER_OPTIONS.find((o) => o.value === containerValue);
  if (!option) return "";
  // Build a display string like "Peak Milk Can (180 mL)"
  // The label key is used by i18n for translation support
  return `${containerValue.replace(/_/g, " ").split(" ").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ")} (${option.volumeMl} mL)`;
}

// ──────────────────────────────────────────────────────────────────────────────────
// Get the human-readable name for a fertilizer type with its density
// For example: urea → "Urea (1.32 g/mL)"
// ──────────────────────────────────────────────────────────────────────────────────
export function getFertilizerTypeLabel(fertilizerTypeValue) {
  const option = OFRA_FERTILIZER_TYPE_OPTIONS.find((o) => o.value === fertilizerTypeValue);
  if (!option) return "";
  // Build a display string like "Urea (1.32 g/mL)"
  // Convert underscore-separated value to Title Case
  const name = fertilizerTypeValue
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  return `${name} (${option.densityGPerMl} g/mL)`;
}

// ──────────────────────────────────────────────────────────────────────────────────
// Get the human-readable name for an application method
// For example: point → "Point"
// ──────────────────────────────────────────────────────────────────────────────────
export function getApplicationMethodLabel(applicationMethodValue) {
  const option = OFRA_APPLICATION_METHOD_OPTIONS.find(
    (o) => o.value === applicationMethodValue,
  );
  if (!option) return "";
  // Convert value like "point" or "broadcast" to Title Case
  return applicationMethodValue.charAt(0).toUpperCase() + applicationMethodValue.slice(1);
}

// ──────────────────────────────────────────────────────────────────────────────────
// Get the result label based on the application method
// For example: point → "Points per Container"
// This tells the farmer what unit to measure in
// ──────────────────────────────────────────────────────────────────────────────────
export function getResultLabel(applicationMethodValue) {
  const option = OFRA_APPLICATION_METHOD_OPTIONS.find(
    (o) => o.value === applicationMethodValue,
  );
  if (!option) return "";
  if (applicationMethodValue === "point") {
    return "Points per Container";
  } else if (applicationMethodValue === "band" || applicationMethodValue === "broadcast") {
    return "Meters per Container";
  }
  return "";
}
